import { AnimatePresence } from 'motion/react'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import { UIDialog } from 'src/components/ui/Modal'
import { useOnboardingInGenerationPanelExperiment } from 'src/experiments/experiments/2605/onboardingInGenerationPanel'
import { useSocialMedia } from 'src/hooks/socialMedia'
import { useMyInfo } from 'src/services/apollo/auth'
import { useGetMyReferrerId } from 'src/services/apollo/referral'
import { useGetSuggestUsernames } from 'src/services/apollo/user'
import { getErrorCode, getErrorData } from 'src/utils/errorUtil'
import { track, useTrackPageView } from 'src/utils/tracker'
import { useOnboardingData } from './helper'
import { NewUserCharacterOptionsProvider } from './NewUserCharacterOptionsContext'
import type { NewUserOnboardingContextValue } from './NewUserOnboardingContext'
import { NewUserOnboardingContext } from './NewUserOnboardingContext'
import {
  NewUserOnboardingFormProvider,
  useNewUserOnboardingForm,
} from './newUserOnboardingForm'
import { NewUserCategoryStep } from './steps/NewUserCategoryStep'
import { NewUserCharacterStep } from './steps/NewUserCharacterStep'
import { NewUserFinalStep } from './steps/NewUserFinalStep'
import { NewUserFirstPromptRevealStep } from './steps/NewUserFirstPromptRevealStep'
import { NewUserGenderStep } from './steps/NewUserGenderStep'
import { NewUserPromptStyleStep } from './steps/NewUserPromptStyleStep'
import { NewUserUsernameStep } from './steps/NewUserUsernameStep'

const baseSteps: FC[] = [
  NewUserGenderStep,
  NewUserCategoryStep,
  NewUserCharacterStep,
  NewUserPromptStyleStep,
  NewUserUsernameStep,
]
const baseTrackingSections: string[] = [
  'gender',
  'interest',
  'favorite-characters',
  'prompt-style',
  'referal',
]

function NewUserOnboardingDialogContent(props: { onComplete: () => void }) {
  const [stepIndex, setStepIndex] = useState(0)
  const { t } = useTranslation(['user'])

  const { isTreatment } = useOnboardingInGenerationPanelExperiment()
  const onboardingSteps = useMemo<FC[]>(
    () => [
      ...baseSteps,
      isTreatment ? NewUserFirstPromptRevealStep : NewUserFinalStep,
    ],
    [isTreatment],
  )
  const trackingSection = useMemo<string[]>(
    () => [...baseTrackingSections, isTreatment ? 'first-prompt' : 'demo'],
    [isTreatment],
  )

  const toastGenericError = (err: unknown) => {
    if (err instanceof Error) toast.error(err.message)
    else toast.error(String(err))
  }

  const toastOnboardingError = (err: unknown) => {
    const code = getErrorCode(err as any)
    if (code === 'PROHIBITED_USERNAME') {
      const field = getErrorData(err as any)?.field
      const key =
        field === 'username'
          ? 'user:profile.notice.username-prohibited'
          : 'user:profile.notice.display-name-prohibited'
      toast.error(t(key))
    } else {
      toastGenericError(err)
    }
  }

  const goToNextStep = useCallback(() => {
    if (stepIndex + 1 >= onboardingSteps.length) {
      props.onComplete()
    } else {
      setStepIndex(stepIndex + 1)
    }
  }, [stepIndex, props.onComplete])

  const [{ loading: goToNextStepWithActionBusy }, goToNextStepWithAction] =
    useAsyncFn(
      async (action?: () => Promise<void>) => {
        if (action) {
          try {
            await action()
          } catch (err: unknown) {
            // Don't proceed on any error
            toastOnboardingError(err)
            return
          }
        }
        goToNextStep()
      },
      [goToNextStep],
    )
  const backToLastStep = useCallback(() => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1)
    }
  }, [stepIndex])
  const form = useNewUserOnboardingForm()
  const skipStep = useCallback(() => {
    track('onboarding_skip', { section: trackingSection[stepIndex] })
    setStepIndex(4)
  }, [stepIndex])

  const userInfoRes = useMyInfo({
    context: {
      refetchOnFocus: false,
    },
  })
  const getMyReferrerIdRes = useGetMyReferrerId({})
  const onboardingDataRes = useOnboardingData()
  const socialMediaDataRes = useSocialMedia()
  const getSuggestUsernamesRes = useGetSuggestUsernames({})

  const usernameInitialized = useRef(false)
  useEffect(() => {
    if (usernameInitialized.current) {
      return
    }
    if (!getSuggestUsernamesRes.loading && getSuggestUsernamesRes.data) {
      const username = getSuggestUsernamesRes.data.at(0)
      if (username) {
        form.setFieldValue('username', username)
      }
      usernameInitialized.current = true
    }
  }, [getSuggestUsernamesRes.data, getSuggestUsernamesRes.loading])

  const trackPageView = useTrackPageView()
  useEffect(() => {
    trackPageView({
      scene: 'onboarding',
      section: trackingSection[stepIndex],
      modal: true,
    })
  }, [stepIndex])

  const value: NewUserOnboardingContextValue = useMemo(
    () => ({
      goToNextStepWithAction,
      backToLastStep,
      skipStep,
      actionBusy: goToNextStepWithActionBusy,
      currentStep: stepIndex,
      totalStep: onboardingSteps.length - 1,
      userInfoRes,
      getMyReferrerIdRes,
      getSuggestUsernamesRes,
      onboardingDataRes,
      socialMediaDataRes,
    }),
    [
      stepIndex,
      goToNextStep,
      backToLastStep,
      skipStep,
      goToNextStepWithAction,
      userInfoRes,
      getMyReferrerIdRes,
      getSuggestUsernamesRes,
      onboardingDataRes,
      socialMediaDataRes,
    ],
  )
  const CurrentStep = onboardingSteps[stepIndex]

  return (
    <NewUserOnboardingFormProvider form={form}>
      <NewUserOnboardingContext.Provider value={value}>
        <NewUserCharacterOptionsProvider>
          <div className="w-[512px] h-[640px] max-w-[calc(100vw-32px)] relative">
            <AnimatePresence initial={false}>
              <CurrentStep key={stepIndex} />
            </AnimatePresence>
          </div>
        </NewUserCharacterOptionsProvider>
      </NewUserOnboardingContext.Provider>
    </NewUserOnboardingFormProvider>
  )
}

export async function openNewUserOnboardingDialog() {
  return UIDialog.create({
    dismissable: false,
    className: 'min-w-[unset] max-w-[unset] overflow-clip',
    children: ({ close }) => (
      <NewUserOnboardingDialogContent onComplete={close} />
    ),
  })
}
